import { EntityState } from "@reduxjs/toolkit";
import { Vector2D } from "common/types";
import { ComponentTypeIds, ComponentCategory, ComponentProperty } from "common/components";
import { ProjectReaction } from "features/reaction/types";

export enum ProjectType {
  BASIC = "basic",
  MAP = "map",
  TEMPLATE = "template",
}

export type ProjectEntity = {
  project_id: string;
  inquiry_id: string;
  type: ProjectType;
  tags: string;
  category: string;
  author_uid: string;
  author_name: string;
  author_icon: string;
  copied_project_id: string;
  original_project_id: string;
  original_project_created_at: string;
  original_author_uid: string;
  original_author_name: string;
  original_author_icon: string;
  name: string;
  version: number;
  description: string;
  capture?: string;
  cover?: string;
  blockly_capture: string;
  reaction: ProjectReaction;
  published: boolean;
  copied: number;
  favorited: number;
  viewed: number;
  enable_copied: boolean;
  /** 公式のアプリ。これまでは作者の表示名「まなパズ公式」で判定していた */
  is_official: boolean;
  /** 管理画面で処分（ブロック）されたアプリ。公開できない */
  banned: boolean;
  banned_at?: string | null;
  is_favorited: boolean;
  created_time: string;
  updated_time: string;
  /** 公開日（user_published_project.published_at）。公開していなければ無い */
  published_at?: string | null;
  deleted_at?: string;
  /** 何回目の公開・公開の更新か（V 番号。version は中身の形式の版なので別） */
  completed_version?: number;
  /** 公開中のアプリを編集中（ドラフトがある）。自分の作品の一覧とエディタでだけ返る */
  has_draft?: boolean;
};

/** 戻せる地点ができたきっかけ */
export enum ProjectVersionReason {
  COMPLETE = "complete",
  PUBLISH_UPDATE = "publish_update",
  PUBLISH = "publish",
  CLOSE = "close",
  OPEN = "open",
  BEFORE_RESTORE = "before_restore",
  MIGRATE = "migrate",
  /** エディタを開いたまま 10 分たったとき（中身が変わっていたときだけ残る） */
  AUTO = "auto",
  /** restore したあと */
  RESTORE = "restore",
  /** 「りれきの かくにん」の保存ボタンで、子どもが自分で残した記録 */
  SAVE = "save",
}

export type ProjectVersion = {
  id: string;
  seq: number;
  reason: ProjectVersionReason;
  /** 公開・公開の更新で残した版なら V 番号。途中の記録は null */
  completed_version: number | null;
  /** 「名前をつけて保存」の記録はつけた名前。ほかの記録はその時点のアプリ名 */
  name: string;
  capture: string | null;
  blockly_capture: string | null;
  created_at: string;
};

/** 変更履歴に残す操作（何をしたかだけ。中身は持たない） */
export enum ProjectEditAction {
  ADD_COMPONENT = "add_component",
  REMOVE_COMPONENT = "remove_component",
  ADD_SCREEN = "add_screen",
  REMOVE_SCREEN = "remove_screen",
  CHANGE_PROPERTY = "change_property",
  CHANGE_BLOCKLY = "change_blockly",
  CHANGE_CANVAS = "change_canvas",
  PASTE = "paste",
  UNDO = "undo",
  REDO = "redo",
  /** undo で取り消す対象の名前にだけ使う（restore は記録では区切りになる） */
  RESTORE = "restore",
}

/** undo の履歴に付ける操作の名前。undo / redo を記録に残すときに、何を取り消したかを書く */
export type HistoryStackLabel = {
  action_type: ProjectEditAction;
  target_type?: string | null;
  target_name?: string | null;
};

/** エディタの中身を丸ごと（restore・へんしゅうを すてる の undo 用） */
export type CreatorContentSnapshot = {
  defaultScreenId: string;
  selectedScreenId: string;
  screenSize: ScreenSize["screenSize"];
  canvas: CanvasEntity[];
  screens: ScreensEntity[];
  components: ComponentsEntity[];
  properties: PropertiesEntity[];
  blockly: BlocklyEntity[];
};

export type ProjectEditHistoryEntry = {
  id: number;
  action_type: ProjectEditAction;
  /** undo / redo で取り消した・やり直した操作 */
  undone_action_type: ProjectEditAction | null;
  target_type: string | null;
  target_name: string | null;
  count: number;
  created_at: string;
};

export type ProjectVersionsResponse = {
  versions: ProjectVersion[];
  history: ProjectEditHistoryEntry[];
  /** 版の総数（見られない分も含む） */
  total: number;
  /** 見られる件数。-1 は無制限 */
  limit: number;
  has_draft: boolean;
  completed_version: number;
};

export type CreatorOptions = {
  margin: number;
  headerHeight: number;
  componentListWidth: number;
  tabHeight: number;
  projectId: string;
};

export enum ScreenOrientation {
  LANDSCAPE = "LANDSCAPE",
  PORTRAIT = "PORTRAIT",
}

// 常に1280*960サイズのscaleが１である座標
export type CanvasEntity = {
  id: string;
  x: number;
  y: number;
};

export type ScreenComponent = {
  id: string;
  typeId: string;
  categoryId: string;
  name: string;
};
export type ScreensEntity = {
  id: string;
  name: string;
  order?: number;
  children: ScreenComponent[]; // component id
  childrenOrder: string[]; // component id
};

export type ComponentsEntityData = {
  [componentId: string]: {
    id: string;
    deleted: boolean;
  }[];
};
export type ComponentsEntity = {
  screenId: string;
  data: ComponentsEntityData;
};

export type PropertiesEntity = {
  id: string;
  typeId: string;
  categoryId: string;
  screenId: string;
  property: ComponentProperty;
};

export type BlocklyEntity = {
  screenId: string;
  xmlText?: string;
};

export enum ClipboardTargetType {
  SCREEN = "screen",
  COMPONENT = "component",
  GROUPING = "grouping",
}

export type ClipboardScreen = {
  type: ClipboardTargetType.SCREEN;
  screen: ScreensEntity;
  components: ComponentsEntity;
  propertiesEntities: { [componentId: string]: PropertiesEntity };
};

export type ClipboardComponent = {
  type: ClipboardTargetType.COMPONENT;
  propertiesEntity: PropertiesEntity;
};

export interface ClipboardGrouping {
  screenId: string;
  type: ClipboardTargetType.GROUPING;
  position: Vector2D;
  size: Vector2D;
  components: { componentId: string; propertiesEntity: PropertiesEntity }[];
}

export type ClipboardComponentType = ClipboardScreen | ClipboardComponent | ClipboardGrouping;

export enum ActionCommandType {
  ADD_COMPONENT = "ADD_COMPONENT",
  REMOVE_COMPONENT = "REMOVE_COMPONENT",
  ADD_SCREEN = "ADD_SCREEN",
  REMOVE_SCREEN = "REMOVE_SCREEN",
  REMOVE_GROUPING = "REMOVE_GROUPING",
  CHANGE_CANVAS = "CHANGE_CANVAS",
  CHANGE_BLOCKLY = "CHANGE_BLOCKLY",
  CHANGE_PROPERTY = "CHANGE_PROPERTY",
  COPY = "COPY",
  PASTE = "PASTE",
  UNDO = "UNDO",
  REDO = "REDO",
  INSERT_TEMPLATE = "INSERT_TEMPLATE",
}

export interface AddComponentCommand {
  type: ActionCommandType.ADD_COMPONENT;
  screenId: string;
  category: ComponentCategory;
  componentType: ComponentTypeIds;
  position?: Vector2D;
  image?: {
    url: string;
    size: number;
    width: number;
    height: number;
  };
  drawing?: {
    svg: string;
    width: number;
    height: number;
  };
}

export interface RemoveComponentCommand {
  type: ActionCommandType.REMOVE_COMPONENT;
  screenId: string;
  componentId: string;
}

export interface AddScreenCommand {
  type: ActionCommandType.ADD_SCREEN;
}

export interface RemoveScreenCommand {
  type: ActionCommandType.REMOVE_SCREEN;
  screenId: string;
}

export interface RemoveGroupingCommand {
  type: ActionCommandType.REMOVE_GROUPING;
  screenId: string;
  componentIds: string[];
}

export interface ChangeCanvasCommand {
  type: ActionCommandType.CHANGE_CANVAS;
  screenId: string;
  canvas: CanvasEntity;
}

export interface ChangeBlocklyCommand {
  type: ActionCommandType.CHANGE_BLOCKLY;
  screenId: string;
  text: string;
}

export interface ChangePropertyCommand {
  type: ActionCommandType.CHANGE_PROPERTY;
  componentId: string;
  property: ComponentProperty;
  record?: boolean;
}

export interface CopyCommand {
  type: ActionCommandType.COPY;
  targetType: ClipboardTargetType;
}

export interface PasteCommand {
  type: ActionCommandType.PASTE;
  targetType: ClipboardTargetType;
}

export interface UndoCommand {
  type: ActionCommandType.UNDO;
}

export interface RedoCommand {
  type: ActionCommandType.REDO;
}

export interface InsertTemplateCommand {
  type: ActionCommandType.INSERT_TEMPLATE;
  projectId: string;
}

export type ActionCommand =
  | AddComponentCommand
  | RemoveComponentCommand
  | AddScreenCommand
  | RemoveScreenCommand
  | RemoveGroupingCommand
  | ChangeCanvasCommand
  | ChangeBlocklyCommand
  | ChangePropertyCommand
  | CopyCommand
  | PasteCommand
  | UndoCommand
  | RedoCommand
  | InsertTemplateCommand;

export interface ScreenSize {
  screenSize: {
    width: number;
    height: number;
    orientation: ScreenOrientation;
  };
}

export enum DrawingModel {
  PEN = "pen",
  ERASER = "eraser",
}

export type FreeDrawingTool = {
  enable: boolean;
  pen: {
    radius: number;
  };
  eraser: {
    radius: number;
  };
  model: DrawingModel;
  color: string;
  reset: boolean;
};

export const FreeDrawingToolDefault = {
  enable: false,
  pen: {
    radius: 5,
  },
  eraser: {
    radius: 20,
  },
  model: DrawingModel.PEN,
  color: "#ed1228",
  reset: false,
};

type PanelCollapse = {
  material: boolean;
  blocklyWorkspace: boolean;
  property: boolean;
  componentList: boolean;
};

export const PanelCollapseDefault = {
  material: true,
  blocklyWorkspace: true,
  property: true,
  componentList: true,
};

export type Grouping = {
  enable: boolean;
  componentIds: string[];
  position: Vector2D;
  size: Vector2D;
};

export const GroupingDefault = {
  enable: false,
  componentIds: [],
  position: { x: 0, y: 0 },
  size: { x: 0, y: 0 },
};

export type HistoryStackType = {
  label?: HistoryStackLabel;
  /** restore・へんしゅうを すてる は中身を丸ごと差し替える */
  whole?: { before: CreatorContentSnapshot; after: CreatorContentSnapshot };
  before: {
    selectedScreenId?: string;
    selectedComponentId?: string;
    defaultScreenId?: string;
    grouping?: Grouping;
    clipboard?: ClipboardComponentType;
    canvas?: CanvasEntity;
    screen?: ScreensEntity;
    components?: ComponentsEntity;
    properties: PropertiesEntity[];
    blockly?: BlocklyEntity;
  };
  after: {
    selectedScreenId?: string;
    selectedComponentId?: string;
    defaultScreenId?: string;
    grouping?: Grouping;
    clipboard?: ClipboardComponentType;
    canvas?: CanvasEntity;
    screen?: ScreensEntity;
    components?: ComponentsEntity;
    properties: PropertiesEntity[];
    blockly?: BlocklyEntity;
  };
};

export enum ProjectPanelType {
  USER = "USER",
  SHARED = "SHARED",
  TRASHCAN = "TRASHCAN",
  NONE = "NONE",
}

export enum SortedOrder {
  DATE = "date",
  FAVORITED = "favorited",
}

export enum UserSortedOrder {
  DATE = "date",
  VIEWED = "viewed",
  FAVORITED = "favorited",
}

export enum DisplayMode {
  RANKING = "RANKING",
  PICKUP = "PICKUP",
  CAPTURE = "CAPTURE",
  DETAIL = "DETAIL",
  NONE = "NONE",
}

export enum KeyDirection {
  UP = "UP",
  RIGHT = "RIGHT",
  DOWN = "DOWN",
  LEFT = "LEFT",
  NONE = "NONE",
}

/** みんなのアプリの一覧（中身は projects に入れ、ここにはサーバーが返した順の id だけを持つ） */
export interface PublicProjectsState {
  ids: string[];
  hasMore: boolean;
  /** 1 ページ目・続きのどちらかを読み込み中 */
  loading: boolean;
  /** 1 ページ目から取り直し中 */
  reloading: boolean;
  /** いま待っている要求。取り直したら古い要求の結果は捨てる */
  requestId: string | null;
  /** 公開した・公開をやめたときに増やす（一覧が 1 ページ目から取り直す） */
  version: number;
}

export interface CreatorInitialState extends ScreenSize {
  tutorialMenu: { creator_top_menu: boolean; creator_panel_menu: boolean };
  projectPanel: ProjectPanelType;
  panelTabIndex: number;
  keyword: string;
  sortedOrderPanel: boolean;
  projectSortedOrder: SortedOrder;
  publishedProjectFilter: { onlyOfficial: boolean; userFavorited: boolean };
  userProjectSortedOrder: UserSortedOrder;
  userProjectFilter: { userFavorited: boolean };
  cursor: KeyDirection;
  action: { back: boolean; confirm: boolean };
  displayMode: DisplayMode;
  selectedProjectId: string;
  recommendReturnToHome: boolean;
  recommendProject: ProjectEntity;
  isCopyProject: boolean;
  projects: EntityState<ProjectEntity>;
  publicProjects: PublicProjectsState;
  focus: boolean; // キャンパスでスクリーンを切り替えするため
  capture: boolean;
  canvasScale: number; // キャンパスの表示を拡大と縮小を行う値、アプリのスクリーン自体のデータをスケール変更しない、データベースに保存しない
  panelCollapse: PanelCollapse;
  editingProjectId: string;
  selectedScreenId: string;
  selectedComponentId: string;
  defaultScreenId: string;
  grouping: Grouping;
  clipboard: ClipboardComponentType;
  undoStacks: HistoryStackType[];
  redoStacks: HistoryStackType[];
  template: { newComponentIds: string[]; newBlockIds: string[] };
  drawing: FreeDrawingTool;
  canvas: EntityState<CanvasEntity>;
  screens: EntityState<ScreensEntity>;
  components: EntityState<ComponentsEntity>;
  properties: EntityState<PropertiesEntity>;
  blockly: EntityState<BlocklyEntity>;
}

export enum TabIndex {
  NONE = "none",
  EVENT = "event",
  BASIC = "basic",
  SIZE = "size",
  POSITION = "position",
  APPEARANCE = "appearance",
  ANIMATION = "animation",
  SOUND = "sound",
  CAMERA = "camera",
  COLOR = "color",
  CONTROL = "control",
  LOGIC = "logic",
  MATH = "math",
  VARIABLE = "variable",
  PROCEDURE = "procedure",
}

export enum StockTabIndex {
  ALBUM = "ALBUM",
  DRAWING = "DRAWING",
  AI = "AI",
  STAMP = "STAMP",
}

/** ピックアップのアプリ。コメントは AI が書いたもの（キーは i18n の言語） */
export type PickupProjectEntity = ProjectEntity & {
  pickup_comment?: Record<string, string>;
};

/** GET /v1/pickup/projects */
export type PickupResponse = {
  projects: PickupProjectEntity[];
  /** 公開中の組。activated_at は「置き換える」を押した日時（見出しの「◯がつの」に使う） */
  round: { activated_at: string } | null;
};
